import React, { useEffect, useState, useRef } from 'react';
import Layout from '../../components/layout/Layout';
import 'bootstrap/dist/css/bootstrap.min.css';
import Modal from 'react-bootstrap/Modal';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    IconButton, Button, TextField, Select, MenuItem, Pagination, InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Progress from '../../hooks/useProgressBar';
import useDebounce from '../../hooks/useDebounce';
import { getData, deleteData, getPresignedUrl, uploadToS3, postData, putData, multipartUploadFile } from '../../services/apiService';

const AddLesson = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [allSections, setAllSections] = useState([]);
    const [filteredSections, setFilteredSections] = useState([]);
    const [quizData, setquizData] = useState([]);
    const [showLessionModal, addLessionShowModal] = useState(false);
    const [updateLessionModalShow, updateLessionModal] = useState(false);
    const [formData, setFormData] = useState({ course: "", section: "", title: "", type: "", content: "", duration: "", order: "" });
    const [selectedLession, setSelectedLession] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [updateFile, setUpdateFile] = useState(null);
    const [courseData, setCourseData] = useState([]);
    const [uploadPercent, setUploadPercent] = useState(0);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [totalPages, setTotalPages] = useState(1);

    // Initial data load for dropdowns
    useEffect(() => {
        getSectionData();
        getquizData();
        getCourse();
    }, []);

    // Fetch lessons when page, page size or search changes
    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, debouncedSearchQuery]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await getData(`/get-all-lession?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}`);
            if (response) {
                setData(response.data);
                setTotalPages(response.totalPages);
            }
        } catch (error) {
            console.error('Failed to fetch data', error);
        } finally {
            setLoading(false);
        }
    };

    const getSectionData = async () => {
        try {
            const response = await getData('/get-sections-for-dropdown');
            if (response) {
                setAllSections(response);
                setFilteredSections(response);
            }
        } catch (error) {
            console.error('Failed to fetch sections', error);
        }
    };

    const getCourse = async () => {
        try {
            const response = await getData('/get-courses-for-dropdown');
            if (response) {
                setCourseData(response);
            }
        } catch (error) {
            console.error('Failed to fetch courses', error);
        }
    };

    const getquizData = async () => {
        try {
            const response = await getData('/get-all-quiz');
            setquizData(Array.isArray(response) ? response : response.data || []);
        } catch (error) {
            console.error('Failed to fetch quiz', error);
            setquizData([]);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => setPage(newPage);
    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    // Handle course selection change
    const handleCourseChange = (e) => {
        const courseId = e.target.value;
        setFormData({
            ...formData,
            course: courseId,
            section: ""
        });

        if (courseId) {
            const filtered = allSections.filter(section => section.course._id === courseId);
            setFilteredSections(filtered);
        } else {
            setFilteredSections(allSections);
        }
    };

    // Handle course change in update modal
    const handleUpdateCourseChange = (e) => {
        const courseId = e.target.value;
        setSelectedLession({
            ...selectedLession,
            course: courseId,
            section: ""
        });

        if (courseId) {
            const filtered = allSections.filter(section => section.course._id === courseId);
            setFilteredSections(filtered);
        } else {
            setFilteredSections(allSections);
        }
    };

    // ===== S3 File Upload Handling =====
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUpdateFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUpdateFile(file);
        }
    };

    const uploadFile = async (file) => {
        try {
            // Use multipart for large files for significantly faster, parallel uploads
            const MULTIPART_THRESHOLD_BYTES = 10 * 1024 * 1024; // 10MB
            if (file.size >= MULTIPART_THRESHOLD_BYTES) {
                return await multipartUploadFile(file, { partSize: 8 * 1024 * 1024, concurrency: 4, onProgress: setUploadPercent });
            }
            const { uploadUrl, fileKey, publicUrl } = await getPresignedUrl(file.type);
            await uploadToS3(uploadUrl, file, {
                onProgress: ({ loaded, total }) => {
                    const percent = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
                    setUploadPercent(percent);
                }
            });
            return { fileKey, publicUrl };
        } catch (err) {
            console.error("S3 Upload Error:", err);
            throw err;
        }
    };

    const handleInputChange = (e) => {
        if (e.target.name === 'type') {
            setFormData({ ...formData, type: e.target.value, content: "" });
            setSelectedFile(null);
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    };

    const addLession = async (e) => {
        e.preventDefault();
        // setLoading(true);
        try {
            let content = formData.content;
            let fileKey = null;

            if ((formData.type === 'video' || formData.type === 'pdf') && selectedFile) {
                const { publicUrl, fileKey: uploadedFileKey } = await uploadFile(selectedFile);
                content = publicUrl;
                fileKey = uploadedFileKey;
            }

            const payload = {
                ...formData,
                content,
                ...(fileKey && { fileKey })
            };

            await postData('/create-lession', payload);
            setFormData({
                course: "",
                section: "",
                title: "",
                type: "",
                content: "",
                duration: "",
                order: ""
            });
            setSelectedFile(null);
            setUploadPercent(0);
            addLessionShowModal(false);
            fetchData();
            // setLoading(false);
        } catch (error) {
            console.error('Failed to add lesson', error);
        } finally {
            // setLoading(false);
        }
    };

    const editLession = (id) => {
        const lessionData = data.find(l => l._id === id);
        setSelectedLession({
            ...lessionData,
            course: lessionData.course._id,
        });

        const filtered = allSections.filter(section => (section.course?._id || section.course) === lessionData.course._id);
        setFilteredSections(filtered);

        updateLessionModal(true);
    };

    const updateLession = async () => {
        if (!selectedLession) return;
        // setLoading(true);

        try {
            let content = selectedLession.content;
            let fileKey = selectedLession.fileKey;

            if ((selectedLession.type === 'video' || selectedLession.type === 'pdf') && updateFile) {
                const { publicUrl, fileKey: uploadedFileKey } = await uploadFile(updateFile);
                content = publicUrl;
                fileKey = uploadedFileKey;
            }

            const payload = {
                section: selectedLession.section,
                course: selectedLession.course,
                title: selectedLession.title,
                type: selectedLession.type,
                duration: selectedLession.duration,
                order: selectedLession.order,
                content,
                ...(fileKey && { fileKey })
            };

            await putData(`/update-lession/${selectedLession._id}`, payload);
            fetchData();
            updateLessionModal(false);
            setUpdateFile(null);
            setUploadPercent(0);
            // setLoading(false);
        } catch (error) {
            console.error('Failed to update lesson', error);
        } finally {
            // setLoading(false);
        }
    };

    const deleteLession = async (id) => {
        try {
            await deleteData(`/delete-lession/${id}`);
            fetchData();
        } catch (error) {
            console.error("Delete Error:", error);
        }
    };

    const renderContentInput = (type, value, onChange, isUpdate = false) => {
        switch (type) {
            case 'text':
                return <textarea className="form-control" value={value || ''} onChange={onChange} rows="4" placeholder="Enter content" name="content" />;
            case 'video':
            case 'pdf':
                return <input type="file" className="form-control" accept={type === 'video' ? 'video/*' : '.pdf'} onChange={isUpdate ? handleUpdateFileChange : handleFileChange} name="content" />;
            case 'quiz':
                return (
                    <select className="form-select" name="content" value={value || ''} onChange={onChange}>
                        <option value="">Select Quiz</option>
                        {quizData.map((quiz) => (
                            <option key={quiz?._id} value={quiz?._id}>{quiz?.title}</option>
                        ))}
                    </select>
                );
            default:
                return <input type="text" className="form-control" value={value || ''} onChange={onChange} placeholder="Content" name="content" />;
        }
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>Lesson Data</Typography>
                    {/* Header: Add Button (left) and Search (right) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                        <Button variant="contained" sx={{
                            borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                            '&:hover': { backgroundColor: '#3A3897', },
                        }} onClick={() => {
                            addLessionShowModal(true); setFilteredSections(allSections);
                        }}> + Add Lesson </Button>
                        <TextField placeholder="Search Lession" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                            sx={{ width: 350, bgcolor: '#e9ecef', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, }, }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                            }}
                        />
                    </Box>
                    {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}

                    <TableContainer component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Course </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Section </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Title </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Type </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Content </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Estimated Duration </TableCell>
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Order </TableCell> */}
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}> Action </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={item._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.course.courseName}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.section.title}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.title}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.type}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.content}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.duration}</Typography>
                                        </TableCell>
                                        {/* <TableCell align="center">
                                            <Typography variant="body1">{item.order}</Typography>
                                        </TableCell> */}
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editLession(item._id)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteLession(item._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>

                    {/* Lesson Add model */}
                    <Modal show={showLessionModal} onHide={() => addLessionShowModal(false)} style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Add Lesson</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <form onSubmit={addLession}>
                                <div className="mb-3">
                                    <label className="col-form-label">Course:</label>
                                    <select
                                        className="form-select"
                                        name="course"
                                        value={formData.course}
                                        onChange={handleCourseChange}
                                        required
                                    >
                                        <option value="">Select Course</option>
                                        {courseData.map((course) => (
                                            <option key={course._id} value={course._id}>{course.title}</option>
                                        ))}
                                    </select>

                                    <label className="col-form-label">Section:</label>
                                    <select
                                        className="form-select"
                                        name="section"
                                        value={formData.section}
                                        onChange={handleInputChange}
                                        required
                                        disabled={!formData.course}
                                    >
                                        <option value="">Select Section</option>
                                        {filteredSections.map((section) => (
                                            <option key={section._id} value={section._id}>{section.title}</option>
                                        ))}
                                    </select>

                                    <label htmlFor="title" className="col-form-label">Title:</label>
                                    <input type="text" className="form-control" id="title" placeholder="Title" name='title' value={formData.title} onChange={handleInputChange} required />

                                    <label className="col-form-label">Type:</label>
                                    <select className="form-select" name="type" value={formData.type} onChange={handleInputChange} required>
                                        <option value="">Select Type</option>
                                        <option value="video">Video</option>
                                        <option value="quiz">Quiz</option>
                                        <option value="text">Text</option>
                                        <option value="pdf">PDF</option>
                                    </select>

                                    <label htmlFor="content" className="col-form-label">Content:</label>
                                    {renderContentInput(
                                        formData.type,
                                        formData.content,
                                        handleInputChange
                                    )}
                                    {selectedFile && (
                                        <div className="mt-2 text-muted">Selected file: {selectedFile.name}</div>
                                    )}
                                    {(formData.type === 'video' || formData.type === 'pdf') && selectedFile ? (
                                        <div className="mt-2">
                                            <div className="progress" style={{ height: 6 }}>
                                                <div className="progress-bar" role="progressbar" style={{ width: `${uploadPercent}%` }} aria-valuenow={uploadPercent} aria-valuemin="0" aria-valuemax="100"></div>
                                            </div>
                                            <div className="small text-muted mt-1">{uploadPercent}%</div>
                                        </div>
                                    ) : null}

                                    <label htmlFor="duration" className="col-form-label">Estimated Duration:</label>
                                    <input type="number" className="form-control" id="duration" placeholder="Duration" name='duration' value={formData.duration} onChange={handleInputChange} required />

                                    {/* <label htmlFor="order" className="col-form-label">Order:</label>
                                    <input type="number" className="form-control" id="order" placeholder="Order" name='order' value={formData.order} onChange={handleInputChange} required /> */}
                                </div>
                                <Modal.Footer>
                                    <Button variant="contained" type="submit" color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>Save</Button>
                                </Modal.Footer>
                            </form>
                        </Modal.Body>
                    </Modal>

                    {/* Lession update model */}
                    <Modal show={updateLessionModalShow} onHide={() => {
                        updateLessionModal(false);
                        setUpdateFile(null);
                    }} style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Update Lesson</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <form>
                                <div className="mb-3">
                                    <label className="col-form-label">Course:</label>
                                    <select className="form-select" name="course" value={selectedLession?.course || ""}
                                        onChange={handleUpdateCourseChange} required
                                    >
                                        <option value="">Select Course</option>
                                        {courseData.map((course) => (
                                            <option key={course._id} value={course._id}>{course.title}</option>
                                        ))}
                                    </select>

                                    <label className="col-form-label">Section:</label>
                                    <select className="form-select" name="section"
                                        value={selectedLession?.section?._id || selectedLession?.section || ""}
                                        onChange={(e) => setSelectedLession({ ...selectedLession, section: e.target.value })}
                                        required disabled={!selectedLession?.course}
                                    >
                                        <option value="">Select Section</option>
                                        {filteredSections.map((section) => (
                                            <option key={section._id} value={section._id}>{section.title}</option>
                                        ))}
                                    </select>

                                    <label htmlFor="title" className="col-form-label">Title:</label>
                                    <input type="text" className="form-control" value={selectedLession?.title || ""} onChange={(e) => setSelectedLession({ ...selectedLession, title: e.target.value })} required />

                                    <label className="col-form-label">Type:</label>
                                    <select className="form-select" name="type" value={selectedLession?.type} onChange={(e) => {
                                        setSelectedLession({ ...selectedLession, type: e.target.value, content: "" });
                                        setUpdateFile(null);
                                    }} required>
                                        <option value="">Select Type</option>
                                        <option value="video">Video</option>
                                        <option value="quiz">Quiz</option>
                                        <option value="text">Text</option>
                                        <option value="pdf">PDF</option>
                                    </select>

                                    <label htmlFor="content" className="col-form-label">Content:</label>
                                    {renderContentInput(
                                        selectedLession?.type,
                                        selectedLession?.content,
                                        (e) => setSelectedLession({ ...selectedLession, content: e.target.value }),
                                        true
                                    )}
                                    {updateFile && (
                                        <div className="mt-2 text-muted">New file selected: {updateFile.name}</div>
                                    )}
                                    {(selectedLession?.type === 'video' || selectedLession?.type === 'pdf') && updateFile ? (
                                        <div className="mt-2">
                                            <div className="progress" style={{ height: 6 }}>
                                                <div className="progress-bar" role="progressbar" style={{ width: `${uploadPercent}%` }} aria-valuenow={uploadPercent} aria-valuemin="0" aria-valuemax="100"></div>
                                            </div>
                                            <div className="small text-muted mt-1">{uploadPercent}%</div>
                                        </div>
                                    ) : null}
                                    {selectedLession?.type === 'video' || selectedLession?.type === 'pdf' ? (
                                        <div className="mt-2 text-muted">Current file: {selectedLession?.content}</div>
                                    ) : null}

                                    <label htmlFor="duration" className="col-form-label">Estimated Duration:</label>
                                    <input type="number" className="form-control" name='duration' value={selectedLession?.duration} onChange={(e) => setSelectedLession({ ...selectedLession, duration: e.target.value })} required />

                                    {/* <label htmlFor="order" className="col-form-label">Order:</label>
                                    <input type="number" className="form-control" value={selectedLession?.order || ""} onChange={(e) => setSelectedLession({ ...selectedLession, order: e.target.value })} required /> */}
                                </div>
                                <Modal.Footer>
                                    <Button variant="contained" onClick={updateLession} color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>Save changes</Button>
                                </Modal.Footer>
                            </form>
                        </Modal.Body>
                    </Modal>
                </Box>
            }
        </Layout>
    );
};

export default AddLesson;